"use client"
/********************************************************************
 * PREFERRED-USE SCORING HELPER (Dual Intent + Averaged Score)
 ********************************************************************/

const ACQUIRE_PRIORITY = { BORROW: 10, RENT: 25, BUY: 50 };

const DISCARD_USED_PRIORITY = {
  SELL: 10,
  DONATE: 20,
  RECYCLE: 40,
  COMPOST: 40,
  DISPOSE: 50
};

const DISCARD_UNUSABLE_PRIORITY = { RECYCLE: 10, COMPOST: 10, DISPOSE: 50 };

const REPAIR_SCORE = 30;
const FALLBACK_SCORE = 200;

/**
 * Score a single action-condition pair for a given intent
 */
function scorePair({ action, condition, intent }) {
  if (action === "REPAIR") return REPAIR_SCORE;

  if (intent === "ACQUIRE") {
    return ACQUIRE_PRIORITY[action] ?? FALLBACK_SCORE;
  }

  if (intent === "DISCARD") {
    if (condition === "USED") {
      return DISCARD_USED_PRIORITY[action] ?? FALLBACK_SCORE;
    }
    if (condition === "USED_NOT_REUSABLE") {
      return DISCARD_UNUSABLE_PRIORITY[action] ?? FALLBACK_SCORE;
    }
  }

  return FALLBACK_SCORE;
}

/**
 * Compute the best (lowest) score for a listing for a given intent
 */
function scoreServiceListing(serviceListing, intent) {
  let best = FALLBACK_SCORE;

  for (const action of serviceListing.actions) {
    for (const condition of serviceListing.conditions) {
      const s = scorePair({ action, condition, intent });
      if (s < best) best = s;
    }
  }

  return best;
}

/**
 * Main driver function
 * @param {Array} businesses - list of business objects
 */
function applyBestUseScores(businesses, searchOption) {
  for (const biz of businesses) {
    const listings = biz?.serviceProfile?.serviceListings ?? [];

    for (const listing of listings) {
      // check if the listing we're looking at has searchOption.value in categories or items
      const hasSearchOption =
        listing.categories.some(category =>
          category.includes(searchOption.value)
        ) || listing.items.some(item => item.includes(searchOption.value));

      if (!hasSearchOption) {
        listing.bestUseScoreAcquire = FALLBACK_SCORE;
        listing.bestUseScoreDiscard = FALLBACK_SCORE;
        listing.bestUseScoreAverage = FALLBACK_SCORE;
        continue;
      }

      const baseAcquire = scoreServiceListing(listing, "ACQUIRE");
      const baseDiscard = scoreServiceListing(listing, "DISCARD");

      const adjustedAcquire = baseAcquire;
      const adjustedDiscard = baseDiscard;

      listing.bestUseScoreAcquire = adjustedAcquire;
      listing.bestUseScoreDiscard = adjustedDiscard;
      listing.bestUseScoreAverage = (adjustedAcquire + adjustedDiscard) / 2;
    }
  }
}

/* Example Usage */
// applyBestUseScores(searchResults);
export { applyBestUseScores };
